import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Album } from '../types';
import { ContentRating } from '../types/Tags';
import { RecordingManager } from './RecordingManager';
import { withDemoModeFallback } from './base/demoModeUtils';
import { DemoDataManager } from '../services/demoDataManager';
import { BaseCRUDManager } from './base/BaseCRUDManager';

// Define TypeScript interfaces for your album and other types as needed

export const AlbumManager = {
  getAllAlbums: async (): Promise<Album[]> => {
    try {
      return (await BaseCRUDManager.getAll<any>('albums', {
        idField: 'albumKey'
      })) as Album[];
    } catch (error) {
      console.error("Error getting albums:", error);
      return [];
    }
  },

  getAlbum: async (albumKey: string): Promise<Album | null> => {
    try {
      return (await BaseCRUDManager.getById<any>('Albums', albumKey, {
        idField: 'albumKey'
      })) as Album | null;
    } catch (error) {
      console.error("Error fetching album:", error);
      return null;
    }
  },

  removeAlbum: async (albumKey: string): Promise<void> => {
    await withDemoModeFallback(
      async () => {
        DemoDataManager.deleteAlbum(albumKey);
      },
      async () => {
        try {
          await BaseCRUDManager.delete('Albums', albumKey);
        } catch (error) {
          console.error("Error removing album:", error);
          throw error;
        }
      }
    );
  },

  getAlbumByPinKey: async (albumKey: string): Promise<Album | null> => {
    try {
      return (await BaseCRUDManager.getById<any>('Albums', albumKey, {
        idField: 'albumKey'
      })) as Album | null;
    } catch (error) {
      console.error("Error fetching album by album key:", error);
      return null;
    }
  },

  createAlbum: async (newAlbum: Album): Promise<Album> => {
    return await withDemoModeFallback(
      async () => DemoDataManager.createAlbum(newAlbum),
      async () => {
        try {
      const { albumKey, ...albumData } = newAlbum;
      // Keep albumKey on the document — Firestore rules require
      // request.resource.data.albumKey == album (doc id).
      const createdId = await BaseCRUDManager.create(
        'Albums',
        { ...albumData, albumKey },
        {
          idField: 'albumKey',
          customId: albumKey || undefined,
          autoTimestamps: false, // Albums may have their own timestamp handling
        }
      );
        return { ...newAlbum, albumKey: createdId };
      } catch (error) {
        console.error("Error creating album:", error);
        throw error; // Rethrow the error to be handled by the caller
      }
      }
    );
  },

  updateAlbumContentRating: async (albumKey: string): Promise<void> => {
    try {
      // Get all recordings for this album
      const recordings = await RecordingManager.fetchRecordingsByAlbumKey(albumKey);
      
      // Track all unique content ratings and calculate highest rating
      const uniqueRatings = new Set<ContentRating>();
      let highestRating: ContentRating = 'Family';
      let hasAdultContent = false;
      let hasMixedContent = false;
      
      // First pass: collect all ratings
      for (const recording of recordings) {
        if (recording.contentRating) {
          uniqueRatings.add(recording.contentRating);
          if (recording.contentRating === 'Adult') {
            hasAdultContent = true;
            highestRating = 'Adult';
          } else if (recording.contentRating === 'Young Adult' && highestRating === 'Family') {
            highestRating = 'Young Adult';
          }
        }
      }

      // Determine if content is mixed (has both adult and non-adult content)
      if (hasAdultContent && uniqueRatings.size > 1) {
        hasMixedContent = true;
      }

      // Convert Set to array for storage
      const contentRatings = Array.from(uniqueRatings);

      // Update album document
      const albumRef = doc(db, 'Albums', albumKey);
      await updateDoc(albumRef, {
        contentRating: highestRating,
        hasAdultContent,
        hasMixedContent,
        contentRatings
      });

    } catch (error) {
      console.error("Error updating album content rating:", error);
      throw error;
    }
  },
};

export default AlbumManager;
